import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import productService from '../services/productService';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import ProductCard from '../components/Catalog/ProductCard';

const Home = () => {
  const { t } = useTranslation();
  const [mainCollections, setMainCollections] = useState([]);
  const [categories, setCategories] = useState([]);
  const [favoriteProducts, setFavoriteProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchFavoriteProducts = async () => {
    const savedFavorites = JSON.parse(localStorage.getItem('favoriteProducts') || '[]');
    if (savedFavorites.length > 0) {
      // In a real app, we would fetch these products by their IDs
      // For now, we'll simulate by getting some products
      const productsResponse = await productService.getProducts();
      const favorites = productsResponse.data.filter(p => savedFavorites.includes(p.id));
      setFavoriteProducts(favorites);
    } else {
      setFavoriteProducts([]);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch all collections from API
        const collectionsResponse = await productService.getCollections();
        
        // Handle both API response formats
        const collectionsArray = collectionsResponse.data.collections || collectionsResponse.data;
        
        // Filter to get only main collections (parent_id is null)
        const mainCollectionsData = collectionsArray.filter(col => !col.parent_id);
        setMainCollections(mainCollectionsData);
        
        // Get Joalharia subcollections as categories (parent_id = 24002)
        const joalhariaCategories = collectionsArray.filter(col => col.parent_id === 24002);
        
        // Fetch all products to get accurate counts
        const productsResponse = await productService.getProducts({ limit: 1000 });
        const allProducts = productsResponse.data || [];
        
        // Add actual product counts to categories
        const categoriesWithCounts = joalhariaCategories.map(cat => ({
          ...cat,
          product_count: allProducts.filter(product => {
            return product.collections?.some(col => col.name === cat.name) || 
                   product.category === cat.name;
          }).length
        }));
        
        // Sort alphabetically
        categoriesWithCounts.sort((a, b) => a.name.localeCompare(b.name));
        setCategories(categoriesWithCounts);
        
        // Load favorite products
        await fetchFavoriteProducts();
      } catch (err) {
        console.error('Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Listen for favorites updates
  useEffect(() => {
    const handleFavoritesUpdate = () => {
      fetchFavoriteProducts();
    };

    window.addEventListener('favoritesUpdated', handleFavoritesUpdate);
    return () => {
      window.removeEventListener('favoritesUpdated', handleFavoritesUpdate);
    };
  }, []);


  const getCollectionDescription = (name) => {
    const descriptions = {
      'Anéis': t('home.category_descriptions.rings'),
      'Brincos': t('home.category_descriptions.earrings'),
      'Colares': t('home.category_descriptions.necklaces'),
      'Pulseiras': t('home.category_descriptions.bracelets'),
      'Talismãs': t('home.category_descriptions.talismans'),
      'Perneiras': t('home.category_descriptions.anklets'),
      'Joalharia': 'Coleção completa de joias exclusivas',
      'Coleção Cápsula': 'Edições limitadas e colaborações especiais',
      'Laura Figueiredo': 'Coleção exclusiva por Laura Figueiredo'
    };
    return descriptions[name] || name;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-light text-bmb-text mb-4">{t('home.title')}</h1>
            <p className="text-gray-600 text-lg mb-6">{t('home.subtitle')}</p>
            <Link to="/catalog">
              <button className="px-8 py-3 bg-bmb-text text-white rounded-lg hover:bg-primary-700 transition-colors mb-8">
                {t('home.view_full_catalog')}
              </button>
            </Link>
            
            {/* Categories Grid */}
            {categories.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4 max-w-4xl mx-auto mt-8">
                {categories.map((category) => (
                  <Link
                    key={category.id}
                    to={`/catalog?category=${category.name}`}
                    className="group"
                  >
                    <div className="text-center p-4 border border-gray-200 rounded-lg hover:border-bmb-text hover:bg-gray-50 transition-all duration-200">
                      <h3 className="text-sm font-medium text-gray-700 group-hover:text-bmb-text transition-colors">
                        {category.name}
                      </h3>
                      {category.product_count > 0 && (
                        <p className="text-xs text-gray-500 mt-1">
                          {category.product_count} {category.product_count === 1 ? t('common.product') : t('common.products')}
                        </p>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Main Collections Sections */}
      {mainCollections.map((mainCollection) => (
        <section key={mainCollection.id} className="py-12 bg-white">
          <div className="container mx-auto px-4">
            {/* Main Collection Header */}
            <div className="mb-8">
              <h2 className="text-3xl font-light text-bmb-text mb-2">{mainCollection.name}</h2>
              <p className="text-gray-600">{getCollectionDescription(mainCollection.name)}</p>
            </div>
            
            {/* Main Collection Link (if exists and has no subcollections) */}
            {(!mainCollection.subcollections || mainCollection.subcollections.length === 0) && (
              <Link
                to={`/catalog?collection=${mainCollection.name}`}
                className="block mb-8 group relative overflow-hidden rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300 bg-gradient-to-br from-gray-50 to-white border border-gray-200"
              >
                <div className="p-8">
                  <h3 className="text-2xl font-light mb-2 text-bmb-text group-hover:text-primary-700 transition-colors">{mainCollection.name}</h3>
                  <p className="text-sm font-medium text-gray-600">
                    {mainCollection.product_count > 0 
                      ? `${mainCollection.product_count} ${t('common.products')}`
                      : 'Em breve'
                    }
                  </p>
                </div>
              </Link>
            )}

            {/* Subcollections Grid */}
            {mainCollection.subcollections && mainCollection.subcollections.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {mainCollection.subcollections.map((subcollection) => (
                  <Link
                    key={subcollection.id}
                    to={`/catalog?${mainCollection.name === 'Joalharia' ? 'category' : 'collection'}=${subcollection.name}`}
                    className="group relative overflow-hidden rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300 bg-gradient-to-br from-gray-50 to-white border border-gray-200"
                  >
                    <div className="p-6">
                      <h3 className="text-2xl font-light mb-2 text-bmb-text group-hover:text-primary-700 transition-colors">{subcollection.name}</h3>
                      <p className="text-sm text-gray-600 mb-2">
                        {getCollectionDescription(subcollection.name)}
                      </p>
                      <p className="text-sm font-medium text-gray-700">
                        {subcollection.product_count > 0 
                          ? `${subcollection.product_count} ${t('common.products')}`
                          : 'Em breve'
                        }
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>
      ))}

      {/* Favorites Section */}
      {favoriteProducts.length > 0 && (
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="mb-8">
              <h2 className="text-3xl font-light text-bmb-text mb-2">{t('home.favorites_section.title')}</h2>
              <p className="text-gray-600">{t('home.favorites_section.description')}</p>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {favoriteProducts.slice(0, 4).map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
            
            {favoriteProducts.length > 4 && (
              <div className="text-center mt-8">
                <Link to="/favorites">
                  <button className="px-6 py-2 border border-bmb-text text-bmb-text rounded-lg hover:bg-bmb-text hover:text-white transition-colors">
                    {t('home.favorites_section.view_all_favorites', { count: favoriteProducts.length })}
                  </button>
                </Link>
              </div>
            )}
          </div>
        </section>
      )}
    </div>
  );
};

export default Home;